import moment from 'moment';

export interface ContactFormValues {
  firstName: string;
  lastName: string;
  role: string;
  phone: string;
  email: string;
}

export interface DepartmentFormValues {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export interface RoleFormValues {
  id: string;
  departmentId: string;
  color: string;
  icon: string;
  roleType: string;
  permissionLevel: string;
  customPermission: boolean;
  permissions: string[];
}

export interface ShiftFormValues {
  id: string;
  from: string;
  to: string;
  name: string;
}

export interface ZoneFormValues {
  id: string;
  name: string;
  shortName: string;
  color: string;
}

export const GENERAL_FOLDER_ID = 'general-checklist-folder' as const;
export const GENERAL_FOLDER_NAME = "כל הצ'קליסטים";

export const GENERAL_REGULATION_FOLDER_ID =
  'general-regulation-folder' as const;
export const GENERAL_REGULATION_FOLDER_NAME = 'כל הרגולציות';

export interface NotifyPlaceSelection {
  clientId: string;
  branchId?: string;
  subBranchId?: string;
  departmentIds: string[] | 'all';
}

export interface OnboardingUserFormValues {
  id: string;
  firstName: string;
  lastName: string;
  clientIds: string[];
  branchIds: string[];
  subBranchIds?: string[];
  departmentId: string[] | 'all';
  roleId: string;
  countryCode: string;
  phone: string;
  email: string;
  sendPassword: boolean;
  sendPasswordVia?: string;
  birthday?: string;
  status: 'active' | 'inactive';
  userPermissions: string[];
  associatedGroups?: string[];
  notifyFromDepts: NotifyPlaceSelection[];
  notifyQuickDepts: NotifyPlaceSelection[];
  notifyTypes: string[];
  enablePersonalNotifications: boolean;
  createdAt?: string;
  updatedAt?: string;
  isAppUser?: boolean;
}

export const createDefaultUser = (): OnboardingUserFormValues => ({
  id: crypto.randomUUID(),
  firstName: '',
  lastName: '',
  clientIds: [],
  branchIds: [],
  subBranchIds: [],
  departmentId: [],
  roleId: '',
  countryCode: '972',
  phone: '',
  email: '',
  sendPassword: false,
  sendPasswordVia: '',
  birthday: '',
  status: 'inactive',
  userPermissions: [],
  associatedGroups: [],
  notifyFromDepts: [],
  notifyQuickDepts: [],
  notifyTypes: [],
  enablePersonalNotifications: false,
  createdAt: undefined,
  updatedAt: undefined,
  isAppUser: false,
});

export interface FolderValues {
  id: string;
  name: string;
  isGeneral?: boolean;
}

export const isGeneralChecklistFolder = (folder: FolderValues): boolean =>
  folder.isGeneral === true;

export const isGeneralRegulationFolder = (folder: FolderValues): boolean =>
  folder.isGeneral === true;

export const isGeneralChecklistFolderId = (
  folderId: string,
  folders: FolderValues[],
): boolean => {
  const folder = folders.find((f) => f.id === folderId);
  return folder ? isGeneralChecklistFolder(folder) : false;
};

export const isGeneralRegulationFolderId = (
  folderId: string,
  folders: FolderValues[],
): boolean => {
  const folder = folders.find((f) => f.id === folderId);
  return folder ? isGeneralRegulationFolder(folder) : false;
};

export const sortFoldersWithGeneralFirst = (
  folders: FolderValues[],
): Array<{ folder: FolderValues; originalIndex: number }> =>
  folders
    .map((folder, originalIndex) => ({ folder, originalIndex }))
    .sort((a, b) => {
      if (a.folder.isGeneral) return -1;
      if (b.folder.isGeneral) return 1;
      return 0;
    });

export interface ChecklistListItemFormValues {
  instruction?: string;
  status: boolean;
}

export interface ChecklistFormValues {
  id: string;
  title: string;
  description?: string;
  status: boolean;
  departments: string[];
  shiftId?: string;
  folderId?: string;
  showInBrief: boolean;
  approvalRequired: boolean;
  enableComments: boolean;
  resetDaily: boolean;
  list: ChecklistListItemFormValues[];
}

export type ChecklistSidebarSelection =
  | { type: 'shift'; id: string }
  | { type: 'folder'; id: string };

export const isGeneralChecklistFolderSelection = (
  selection: ChecklistSidebarSelection | null,
  folders: FolderValues[],
): boolean =>
  selection?.type === 'folder' &&
  isGeneralChecklistFolderId(selection.id, folders);

export const CHECKLIST_NOTES_RESET_TYPES = {
  START_OF_DAY: 'start_of_day',
  END_OF_DAY: 'end_of_day',
  SPECIFIC_TIME: 'specific_time',
} as const;

export type ChecklistNotesResetType =
  (typeof CHECKLIST_NOTES_RESET_TYPES)[keyof typeof CHECKLIST_NOTES_RESET_TYPES];

export interface OperatingHoursFormValues {
  startTime: string;
  endTime: string;
}

export interface ChecklistNotesResetFormValues {
  type: ChecklistNotesResetType;
  time: string;
}

export interface RegulationCustomTimingSettings {
  repeatEvery: number;
  repeatType: string;
  weeklyDays: number[];
  monthlyDates: number[];
  endCondition: string;
  endDate?: string;
  occurrences?: number;
}

export type UploadedMedia = {
  url: string;
  type: 'image' | 'video' | 'audio' | 'document';
};

export type MediaFieldValue = File | UploadedMedia | null;

export interface RegulationFormValues {
  id: string;
  name: string;
  description?: string;
  department?: string;
  assignee?: string;
  type: string;
  changeTypeTime: string;
  sendNotificationBeforeDate: boolean;
  notificationTiming?: string;
  files: Array<File | UploadedMedia>;
  startDate?: string;
  endDate?: string;
  schedulingType: string;
  startTime?: string;
  endTime?: string;
  time?: string;
  folderId: string;
  customTiming?: RegulationCustomTimingSettings | null;
}

export interface BaseNodeFields {
  name: string;
  status?: 'draft' | 'active' | 'inactive';
  isArchived?: boolean;
  type: string;
  logo: MediaFieldValue;
  mobileLogo: MediaFieldValue;
  country: string;
  state: string;
  city: string;
  address: string;
  hpNumber: string;
  website: string;
  phone: string;
  email: string;
  contacts: ContactFormValues[];
  operatingHours: OperatingHoursFormValues;
  checklistNotesReset: ChecklistNotesResetFormValues;
  departments: DepartmentFormValues[];
  roles: RoleFormValues[];
  shifts: ShiftFormValues[];
  zones: ZoneFormValues[];
  zoneDrawing: MediaFieldValue;
  checklistFolders: FolderValues[];
  checklists: ChecklistFormValues[];
  users: OnboardingUserFormValues[];
  regulationFolders: FolderValues[];
  regulations: RegulationFormValues[];
}

export interface SubBranchNodeFormValues extends BaseNodeFields {
  id: string;
}

export interface BranchNodeFormValues extends BaseNodeFields {
  id: string;
  subBranches: SubBranchNodeFormValues[];
}

export interface NetworkNodeFormValues extends BaseNodeFields {
  id: string;
  branches: BranchNodeFormValues[];
}

export interface NetworkDetailsFormValues {
  network: NetworkNodeFormValues[];
}

export type SelectedTreeNode =
  | { level: 'network'; networkIndex: number }
  | { level: 'branch'; networkIndex: number; branchIndex: number }
  | {
      level: 'subBranch';
      networkIndex: number;
      branchIndex: number;
      subBranchIndex: number;
    };

export const getNetworkFieldPath = (networkIndex: number) =>
  `network.${networkIndex}` as const;

export const getBranchFieldPath = (networkIndex: number, branchIndex: number) =>
  `network.${networkIndex}.branches.${branchIndex}` as const;

export const getSubBranchFieldPath = (
  networkIndex: number,
  branchIndex: number,
  subBranchIndex: number,
) =>
  `network.${networkIndex}.branches.${branchIndex}.subBranches.${subBranchIndex}` as const;

export type NetworkNodeFieldPath =
  | ReturnType<typeof getNetworkFieldPath>
  | ReturnType<typeof getBranchFieldPath>
  | ReturnType<typeof getSubBranchFieldPath>;

export const createDefaultDepartment = (
  overrides: Partial<Omit<DepartmentFormValues, 'id'>> = {},
): DepartmentFormValues => ({
  id: crypto.randomUUID(),
  name: '',
  icon: '',
  color: '',
  ...overrides,
});

export const createDefaultRole = (
  departmentId: string,
  overrides: Partial<Omit<RoleFormValues, 'id' | 'departmentId'>> = {},
): RoleFormValues => ({
  id: crypto.randomUUID(),
  departmentId,
  color: '',
  icon: '',
  roleType: '',
  permissionLevel: '',
  customPermission: true,
  permissions: [],
  ...overrides,
});

export const createDefaultShift = (
  overrides: Partial<Omit<ShiftFormValues, 'id'>> = {},
): ShiftFormValues => ({
  id: crypto.randomUUID(),
  from: '',
  to: '',
  name: '',
  ...overrides,
});

export const createDefaultZone = (
  overrides: Partial<Omit<ZoneFormValues, 'id'>> = {},
): ZoneFormValues => ({
  id: crypto.randomUUID(),
  name: '',
  shortName: '',
  color: '',
  ...overrides,
});

export const createGeneralFolder = (): FolderValues => ({
  id: GENERAL_FOLDER_ID,
  name: GENERAL_FOLDER_NAME,
  isGeneral: true,
});

export const createGeneralRegulationFolder = (): FolderValues => ({
  id: GENERAL_REGULATION_FOLDER_ID,
  name: GENERAL_REGULATION_FOLDER_NAME,
  isGeneral: true,
});

export const createDefaultChecklist = (
  overrides: Partial<Omit<ChecklistFormValues, 'id'>> = {},
): ChecklistFormValues => ({
  id: crypto.randomUUID(),
  title: '',
  description: '',
  status: true,
  departments: [],
  showInBrief: false,
  approvalRequired: false,
  enableComments: false,
  resetDaily: true,
  list: [],
  ...overrides,
});

export const createDefaultRegulationFolder = (
  overrides: Partial<Omit<FolderValues, 'id'>> = {},
): FolderValues => ({
  id: crypto.randomUUID(),
  name: '',
  ...overrides,
});

export const createDefaultRegulation = (
  overrides: Partial<Omit<RegulationFormValues, 'id'>> = {},
): RegulationFormValues => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  department: '',
  assignee: '',
  type: 'yellow',
  changeTypeTime: 'day_before',
  sendNotificationBeforeDate: false,
  notificationTiming: '',
  files: [],
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  schedulingType: 'default',
  startTime: '',
  endTime: '',
  time: '',
  folderId: '',
  customTiming: null,
  ...overrides,
});

const baseFields: BaseNodeFields = {
  name: '',
  type: '',
  logo: null,
  mobileLogo: null,
  country: 'israel',
  state: '',
  city: '',
  address: '',
  hpNumber: '',
  website: '',
  phone: '',
  email: '',
  contacts: [
    { firstName: '', lastName: '', role: 'מנכל', phone: '', email: '' },
    { firstName: '', lastName: '', role: 'כספים', phone: '', email: '' },
    { firstName: '', lastName: '', role: 'תפעול', phone: '', email: '' },
  ],
  operatingHours: { startTime: '', endTime: '' },
  checklistNotesReset: {
    type: CHECKLIST_NOTES_RESET_TYPES.START_OF_DAY,
    time: '',
  },
  departments: [],
  roles: [],
  shifts: [],
  zones: [],
  zoneDrawing: null,
  checklistFolders: [createGeneralFolder()],
  checklists: [],
  users: [],
  regulationFolders: [],
  regulations: [],
};

export const createDefaultSubBranch = (): SubBranchNodeFormValues => ({
  id: crypto.randomUUID(),
  ...baseFields,
});

export const createDefaultBranch = (): BranchNodeFormValues => ({
  id: crypto.randomUUID(),
  ...baseFields,
  subBranches: [],
});

export const createDefaultNetwork = (): NetworkNodeFormValues => ({
  id: crypto.randomUUID(),
  ...baseFields,
  branches: [createDefaultBranch()],
});

export const getDefaultNetworkDetailsValues = (): NetworkDetailsFormValues => ({
  network: [createDefaultNetwork()],
});

export const findFieldPathForNodeId = (
  network: NetworkNodeFormValues[],
  nodeId: string,
): NetworkNodeFieldPath | null => {
  for (let i = 0; i < network.length; i++) {
    if (network[i].id === nodeId) return getNetworkFieldPath(i);
    for (let j = 0; j < network[i].branches.length; j++) {
      if (network[i].branches[j].id === nodeId) return getBranchFieldPath(i, j);
      for (
        let k = 0;
        k < (network[i].branches[j].subBranches ?? []).length;
        k++
      ) {
        if (network[i].branches[j].subBranches[k].id === nodeId)
          return getSubBranchFieldPath(i, j, k);
      }
    }
  }
  return null;
};
